import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";

const CATEGORIES = [
  { value: "items", label: "Items", listUrl: "/api/items" },
  { value: "types", label: "Types", listUrl: "/api/types" },
  { value: "maps", label: "Maps", listUrl: "/api/maps" },
  { value: "maps_name", label: "Map Names", listUrl: "/api/maps_name" },
  { value: "event", label: "Events", listUrl: "/api/events" },
];

export default function SubmitTranslations() {
  const [token, setToken] = useState();
  const [languages, setLanguages] = useState([]);
  const [languageId, setLanguageId] = useState(null);
  const [category, setCategory] = useState("types");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showUntranslatedOnly, setShowUntranslatedOnly] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const fetchedToken = await getTokenInternal();
      if (
        !fetchedToken ||
        !["owner", "management", "helper", "linguist"].includes(fetchedToken.account.role)
      ) {
        navigate("/");
        return;
      }
      setToken(fetchedToken);
    };
    init();
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_HOST}/api/languages`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        setLanguages(data);
        const firstNonEnglish = data.find((l) => l.code !== "en");
        setLanguageId((firstNonEnglish || data[0])?.id || 1);
      })
      .catch((err) => console.error("Error fetching languages:", err.message));
  }, []);

  const loadRows = async () => {
    if (!token || !languageId) return;
    setLoading(true);
    try {
      const categoryConfig = CATEGORIES.find((c) => c.value === category);
      const [listRes, lookupRes, pendingRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_HOST}${categoryConfig.listUrl}`),
        fetch(
          `${process.env.REACT_APP_API_HOST}/api/translations/lookup/${category}?language_id=${languageId}`
        ),
        fetch(
          `${process.env.REACT_APP_API_HOST}/api/translations/pending/${category}?language_id=${languageId}`,
          { headers: { Authorization: `Bearer ${token.access_token}` } }
        ),
      ]);

      const list = listRes.ok ? await listRes.json() : [];
      const lookup = lookupRes.ok ? await lookupRes.json() : {};
      const pending = pendingRes.ok ? await pendingRes.json() : {};

      const builtRows = list.map((entry) => {
        const currentTranslation = lookup[entry.name];
        return {
          id: entry.id,
          englishName: entry.name,
          types: entry.types || null,
          currentTranslation,
          isTranslated: currentTranslation && currentTranslation !== entry.name,
          pending: pending[entry.id],
        };
      });
      setRows(builtRows);
    } catch (error) {
      console.error("Error loading translation rows:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [token, category, languageId]);

  const startEdit = (row) => {
    setSubmitError("");
    setEditingId(row.id);
    setEditValue(row.isTranslated ? row.currentTranslation : "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
    setSubmitError("");
  };

  const submitEdit = async (row) => {
    setSubmitError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/translations/submit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            target_table: category,
            target_id: row.id,
            language_id: languageId,
            name: editValue,
            description: null,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Could not submit translation");
      }
      setEditingId(null);
      setEditValue("");
      await loadRows();
    } catch (error) {
      setSubmitError(error.message);
    }
  };

  if (!token) {
    return null;
  }

  const availableTypes = category === "items"
    ? Array.from(new Set(rows.map((r) => r.types).filter(Boolean))).sort()
    : [];

  const filteredRows = rows
    .filter((row) => !showUntranslatedOnly || (!row.isTranslated && !row.pending))
    .filter((row) => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        row.englishName.toLowerCase().includes(q) ||
        (row.currentTranslation && row.currentTranslation.toLowerCase().includes(q))
      );
    })
    .filter((row) => !selectedType || row.types === selectedType)
    .sort((a, b) => a.englishName.localeCompare(b.englishName));

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Submit Translations</h1>

        <div className="mb-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="mr-2 font-bold">Language:</label>
            <select
              value={languageId || ""}
              onChange={(e) => setLanguageId(Number(e.target.value))}
              className="p-1 border rounded text-black"
            >
              {languages.map((language) => (
                <option key={language.id} value={language.id}>
                  {language.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mr-2 font-bold">Category:</label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setSearchTerm("");
                setSelectedType("");
                setShowUntranslatedOnly(false);
              }}
              className="p-1 border rounded text-black"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {category === "items" && availableTypes.length > 0 && (
            <div>
              <label className="mr-2 font-bold">Type:</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="p-1 border rounded text-black"
              >
                <option value="">All Types</option>
                {availableTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mb-4 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-1 border rounded text-black w-56"
          />
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showUntranslatedOnly}
              onChange={(e) => setShowUntranslatedOnly(e.target.checked)}
            />
            Untranslated only
          </label>
          {!loading && (
            <span className="text-gray-400 text-sm">
              {filteredRows.length} of {rows.length}
            </span>
          )}
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : filteredRows.length === 0 ? (
          <p className="text-gray-400">No items match your filters.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-left">
            <thead>
              <tr>
                <th className="pr-4">English</th>
                <th className="pr-4">Translation</th>
                <th className="pr-4">Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td className="pr-4 py-1">{row.englishName}</td>
                  <td className="pr-4 py-1">
                    {editingId === row.id ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="p-1 border rounded text-black"
                      />
                    ) : (
                      row.isTranslated ? row.currentTranslation : "—"
                    )}
                  </td>
                  <td className="pr-4 py-1">
                    {row.pending
                      ? "Pending review"
                      : row.isTranslated
                      ? "Translated"
                      : "Not translated"}
                  </td>
                  <td className="py-1">
                    {row.pending ? (
                      <span className="text-gray-400">Already submitted</span>
                    ) : editingId === row.id ? (
                      <>
                        <button
                          onClick={() => submitEdit(row)}
                          className="mr-2 p-1 border rounded"
                        >
                          Submit
                        </button>
                        <button onClick={cancelEdit} className="p-1 border rounded">
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startEdit(row)}
                        className="p-1 border rounded"
                      >
                        {row.isTranslated ? "Edit" : "Translate"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {submitError && (
          <p className="text-red-500 mt-2">{submitError}</p>
        )}
      </div>
    </div>
  );
}
